import { CsvHeader } from './csvImport'

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

/** Maps App::MultiChoiceCardOrder::RowValidation::ParsingDeliveryError codes to a table column. */
const ERROR_CODE_FIELD: Partial<Record<string, CsvHeader>> = {
  MISSING_FIRSTNAME: 'firstname',
  MISSING_LASTNAME: 'lastname',
  MISSING_EMAIL: 'email',
  INVALID_EMAIL_FORMAT: 'email',
  INVALID_AMOUNT: 'amount'
}

const ERROR_CODE_MESSAGE_KEY: Partial<Record<string, string>> = {
  MISSING_FIRSTNAME: 'newDotationOrder.entry.csvTable.errors.missingFirstname',
  MISSING_LASTNAME: 'newDotationOrder.entry.csvTable.errors.missingLastname',
  MISSING_EMAIL: 'newDotationOrder.entry.csvTable.errors.missingEmail',
  INVALID_EMAIL_FORMAT: 'newDotationOrder.entry.csvTable.errors.invalidEmail',
  INVALID_AMOUNT: 'newDotationOrder.entry.csvTable.errors.invalidAmount'
}

const ERROR_CODE_PATTERN = new RegExp(`(${Object.keys(ERROR_CODE_FIELD).join('|')});(\\d+);`)

export interface CsvSubmitError {
  rowNumber: number
  code: string
  field?: CsvHeader
}

/**
 * Parses backend messages like "Csv data INVALID_EMAIL_FORMAT;2;{"email"=>"..."}"
 * (App::MultiChoiceCardOrder::RowValidation::ParsingDeliveryError, rendered via errors.full_messages).
 */
export function parseCsvSubmitError(message: string): CsvSubmitError | null {
  const match = message.match(ERROR_CODE_PATTERN)
  if (!match) return null

  const [, code, rowNumber] = match
  return { rowNumber: Number(rowNumber), code, field: ERROR_CODE_FIELD[code] }
}

export function csvSubmitErrorMessage(error: CsvSubmitError, t: TranslateFn): string {
  const key = ERROR_CODE_MESSAGE_KEY[error.code]
  return key ? String(t(key)) : error.code
}

export interface PartialRowFailure {
  rowNumber: number
  field?: CsvHeader
  message: string
}

const CUSTOMER_NOT_FOUND_PATTERN = /customer not found/i

/**
 * Maps one entry from App::MultiChoiceCardOrder::CreateOrderLines's `fails` array
 * (line-level failures on an otherwise successful order) into a translated, field-aware failure.
 */
export function mapPartialRowFailure(
  fail: { row_number: number; errors?: string[] },
  t: TranslateFn
): PartialRowFailure | null {
  const rawMessage = fail.errors?.[0]
  if (!rawMessage) return null

  if (CUSTOMER_NOT_FOUND_PATTERN.test(rawMessage)) {
    return {
      rowNumber: fail.row_number,
      field: 'email',
      message: t('newDotationOrder.entry.csvTable.errors.customerNotFound')
    }
  }

  return { rowNumber: fail.row_number, message: rawMessage }
}
